import React, { useState, useEffect } from 'react';
import { Sparkles, X, HelpCircle } from 'lucide-react';
import { areToursDisabled, setToursDisabled } from '../../hooks/useTour';

const HINT_KEY = 'cd365_tour_hint_shown';

interface Props {
  onOpenHelp: () => void;
}

/**
 * One-time elegant popup that asks the user if they want to keep or disable guided tours.
 * Shows once after the first tour has been completed.
 */
export default function TourOptInPopup({ onOpenHelp }: Props) {
  const [visible, setVisible] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const [showPointer, setShowPointer] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed, or if tours are already disabled
    if (localStorage.getItem(HINT_KEY) || areToursDisabled()) return;

    // Only show if at least one tour has been completed
    const hasCompletedTour = Object.keys(localStorage).some(
      (k) => k.startsWith('cd365_tour_done_') && localStorage.getItem(k) === 'true',
    );
    if (!hasCompletedTour) return;

    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setFadingOut(true);
    localStorage.setItem(HINT_KEY, 'true');
    setTimeout(() => setVisible(false), 300);
  };

  const handleDisable = () => {
    setToursDisabled(true);
    dismiss();
  };

  const handleKeep = () => {
    setShowPointer(true);
    localStorage.setItem(HINT_KEY, 'true');
    // Auto-dismiss pointer after a few seconds
    setTimeout(() => {
      setFadingOut(true);
      setTimeout(() => setVisible(false), 300);
    }, 4000);
  };

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 86,
        right: 24,
        zIndex: 1001,
        width: 310,
        opacity: fadingOut ? 0 : 1,
        transform: fadingOut ? 'translateY(10px) scale(0.96)' : 'translateY(0) scale(1)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        animation: !fadingOut ? 'tourPopupIn 0.4s ease-out' : undefined,
      }}
    >
      {/* Arrow pointing to the help button below */}
      <div
        style={{
          position: 'absolute',
          bottom: -8,
          right: 18,
          width: 16,
          height: 16,
          background: 'var(--bg-card, #fff)',
          transform: 'rotate(45deg)',
          borderRight: '1px solid var(--border-light, #e5e5e5)',
          borderBottom: '1px solid var(--border-light, #e5e5e5)',
          zIndex: -1,
        }}
      />

      <div
        style={{
          background: 'var(--bg-card, #fff)',
          borderRadius: 14,
          border: '1px solid var(--border-light, #e5e5e5)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        {/* Header accent */}
        <div
          style={{
            height: 3,
            background: 'linear-gradient(90deg, var(--info, #5B7EA8), var(--primary, #C49A2A))',
          }}
        />

        <div style={{ padding: '16px 16px 14px' }}>
          {/* Close button */}
          <button
            onClick={dismiss}
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted, #999)',
              padding: 4,
              lineHeight: 0,
            }}
            aria-label="Schließen"
          >
            <X size={14} />
          </button>

          {!showPointer ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Sparkles size={18} style={{ color: 'var(--info, #5B7EA8)' }} />
                <span
                  style={{
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    color: 'var(--text, #1A1A2E)',
                  }}
                >
                  Geführte Touren
                </span>
              </div>

              <p
                style={{
                  fontSize: '0.82rem',
                  lineHeight: 1.5,
                  color: 'var(--text-light, #555)',
                  margin: '0 0 14px',
                }}
              >
                Die Touren zeigen dir Schritt für Schritt, wie alles funktioniert.
                Möchtest du sie weiterhin sehen?
              </p>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleKeep}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    borderRadius: 8,
                    border: 'none',
                    cursor: 'pointer',
                    background: 'var(--info, #5B7EA8)',
                    color: '#fff',
                    transition: 'opacity 0.15s',
                  }}
                >
                  Ja, beibehalten
                </button>
                <button
                  onClick={handleDisable}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    borderRadius: 8,
                    border: '1px solid var(--border, #ddd)',
                    cursor: 'pointer',
                    background: 'transparent',
                    color: 'var(--text-light, #555)',
                    transition: 'background 0.15s',
                  }}
                >
                  Ausschalten
                </button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '4px 0' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: 'var(--primary, #C49A2A)',
                  marginBottom: 8,
                }}
              >
                <HelpCircle size={20} style={{ color: '#fff' }} />
              </div>
              <p
                style={{
                  fontSize: '0.82rem',
                  lineHeight: 1.5,
                  color: 'var(--text-light, #555)',
                  margin: 0,
                }}
              >
                Du kannst die Touren jederzeit über den
                <strong style={{ color: 'var(--text, #1A1A2E)' }}> Hilfe-Button </strong>
                unten rechts ein- oder ausschalten.
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes tourPopupIn {
          from {
            opacity: 0;
            transform: translateY(16px) scale(0.94);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}
